import type { Decision } from "../dashboard/deskLogic";
import { viewerNote as deskViewerNote } from "../dashboard/deskLogic";
import { byNumber, byText, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * The librarian's dashboard: work waiting, the overdue list by class,
 * lending over the last fortnight, titles in demand and reading programmes.
 * Mirrors apps/dashboard/librarian.py.
 */

export interface OverdueLoan {
  id: number; borrower: string; admission_number: string; class_name: string; class_teacher: string
  title: string; copy: string; borrowed: string; due: string; days_late: number; fine_to_date: string
}
export interface CirculationDay { date: string; lent: number; returned: number }
export interface LibrarianDesk {
  generated_at: string; today: string; viewer_role: string
  work: Decision[]
  figures: { titles: number; copies: number; on_shelf: number; out: number; overdue: number; lent_today: number; returned_today: number; unpaid_fines: string }
  overdue: OverdueLoan[]
  circulation: CirculationDay[]
  popular: { id: number; title: string; loans: number }[]
  programmes: { id: number; name: string; program_type: string; end_date: string; readers: number; finished: number }[]
}

export const ugx = (value?: string | number | null) => `UGX ${Math.round(Number(value || 0)).toLocaleString("en-GB")}`;

type Tone = { label: string; dot: string };
/** How late a book is, in the words a class teacher would use. */
export function lateness(days: number): Tone {
  if (days >= 28) return { label: `${Math.floor(days / 7)} weeks late`, dot: "bg-red-500" };
  if (days >= 7) return { label: `${Math.floor(days / 7)} ${days < 14 ? "week" : "weeks"} late`, dot: "bg-amber-500" };
  return { label: `${days} ${days === 1 ? "day" : "days"} late`, dot: "bg-sky-500" };
}

export const overdueConfig: RegisterConfig<OverdueLoan> = {
  search: (row) => `${row.borrower} ${row.admission_number} ${row.title} ${row.copy} ${row.class_teacher}`,
  views: [
    { key: "all", label: "All" },
    { key: "week", label: "Over a week", match: (row) => row.days_late >= 7 },
    { key: "month", label: "Over four weeks", match: (row) => row.days_late >= 28 },
  ],
  category: { label: "Class", value: (row) => row.class_name || "No class", inRowOrder: true },
  sorts: [
    { key: "class", label: "By class", compare: () => 0 },
    { key: "late", label: "Latest first", compare: reversed(byNumber((row) => row.days_late)) },
    { key: "borrower", label: "Borrower A–Z", compare: byText((row) => row.borrower) },
  ],
};

/** Lent and returned over the fortnight, and the busiest day. */
export function circulationTotals(days: CirculationDay[]) {
  const lent = days.reduce((sum, row) => sum + row.lent, 0);
  const returned = days.reduce((sum, row) => sum + row.returned, 0);
  const busiest = days.reduce<CirculationDay | null>((best, row) => (row.lent + row.returned > (best ? best.lent + best.returned : 0) ? row : best), null);
  return { lent, returned, busiest };
}

export const viewerNote = (role: string) =>
  role === "librarian" ? "" : deskViewerNote(role, "librarian", "the librarian's dashboard").replace(" The decisions listed are the ones your own account may take, and appraisals are those assigned to you.", " Everything here is the whole library's.");
